import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Badge, Spinner, Dropdown, Modal } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

const DataSp = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('Administrator');
  const [menus, setMenus] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [activeMenu, setActiveMenu] = useState('');
  const [time, setTime] = useState({ date: '', clock: '' });

  // ==========================================
  // STATE DATA SURAT PESANAN (SP)
  // ==========================================
  const [spList, setSpList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLokasiFilter, setSelectedLokasiFilter] = useState('');

  // ==========================================
  // STATE DETAIL SP MODAL
  // ==========================================
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSp, setSelectedSp] = useState(null);
  const [detailSpList, setDetailSpList] = useState([]);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');

  // ==========================================
  // EFFECTS
  // ==========================================
  useEffect(() => {
    const savedUser = localStorage.getItem('nama_user') || localStorage.getItem('user');
    if (savedUser) setUsername(savedUser);

    const savedMenu = localStorage.getItem('mainmenu');
    if (savedMenu) {
      const parsedMenu = JSON.parse(savedMenu);
      setMenus(parsedMenu);
      if (parsedMenu.length > 0) setActiveMenu(parsedMenu[0].id_mn);
    }

    const updateTime = () => {
      const now = new Date();
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      setTime({
        date: now.toLocaleDateString('id-ID', options),
        clock: now.toLocaleTimeString('id-ID') + ' WIB'
      });
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchSp();
  }, []);

  // ==========================================
  // FETCH DATA SP (LIST)
  // ==========================================
  const fetchSp = async () => {
    setIsLoading(true);
    setFetchError('');
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/show_sp?token=${encodeURIComponent(token)}`);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const json = await response.json();
      setSpList(json.list_sp || json.data || []);
    } catch (err) {
      setFetchError('Gagal mengambil data Surat Pesanan (SP): ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================
  // FETCH DETAIL SP (POPUP DETIL)
  // Endpoint: /show_detilsp (POST body JSON nosp)
  // ==========================================
  const fetchDetailSp = async (spItem) => {
    setSelectedSp(spItem);
    setShowDetailModal(true);
    setIsLoadingDetail(true);
    setDetailError('');
    setDetailSpList([]);

    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiUrl();
      const noSpValue = spItem.no_sp || spItem.nosp || '';

      const response = await fetch(`${apiUrl}/show_detilsp?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nosp: noSpValue,
          no_sp: noSpValue,
        }),
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const json = await response.json();
      setDetailSpList(json.list_detilsp || json.list_detailsp || json.data || []);
    } catch (err) {
      console.error('Error fetch detail SP:', err);
      setDetailError('Gagal mengambil rincian detail SP: ' + err.message);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const getMenuIcon = (menuName) => {
    switch (menuName.toUpperCase()) {
      case 'MASTER':        return 'bi-folder2-open';
      case 'GUDANG':        return 'bi-box-seam';
      case 'ADM GUDANG':    return 'bi-building-gear';
      case 'APOTEKER':      return 'bi-prescription2';
      case 'ADMINISTRATOR': return 'bi-person-workspace';
      case 'SETTING':       return 'bi-sliders';
      default:              return 'bi-grid-1x2';
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  // Helper Format Rupiah
  const formatRupiah = (num) => {
    if (num === null || num === undefined || isNaN(num)) return 'Rp 0';
    return 'Rp ' + Number(num).toLocaleString('id-ID');
  };

  // Filter list SP
  const filteredSp = spList.filter((item) => {
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      (item.no_sp && item.no_sp.toLowerCase().includes(q)) ||
      (item.nm_pelanggan && item.nm_pelanggan.toLowerCase().includes(q)) ||
      (item.kd_pelanggan && item.kd_pelanggan.toLowerCase().includes(q)) ||
      (item.nm_lokasi && item.nm_lokasi.toLowerCase().includes(q)) ||
      (item.lokasi && item.lokasi.toLowerCase().includes(q)) ||
      (item.tgl_kirim && item.tgl_kirim.toLowerCase().includes(q)) ||
      (item.user_id && item.user_id.toLowerCase().includes(q));

    const matchesLokasi = !selectedLokasiFilter || item.nm_lokasi === selectedLokasiFilter;

    return matchesSearch && matchesLokasi;
  });

  // Hitung total nominal SP yang terfilter
  const totalNominalSp = filteredSp.reduce((acc, curr) => acc + (Number(curr.total) || 0), 0);

  // Hitung total rincian detail modal
  const totalDetailQty = detailSpList.reduce((acc, curr) => acc + (Number(curr.qty) || 0), 0);
  const totalDetailNominal = detailSpList.reduce((acc, curr) => acc + (Number(curr.total) || 0), 0);

  return (
    <div style={styles.appContainer}>

      {/* ══════════════ NAVBAR ══════════════ */}
      <nav className="navbar navbar-expand-xl navbar-dark shadow-sm" style={styles.navbarCustom}>
        <Container fluid>
          <a className="navbar-brand d-flex align-items-center me-3" href="#home">
            <i className="bi bi-layers-half me-2 text-warning"></i>
            <span className="fw-bold text-white">Solusi Integrasi Layanan Kesehatan ( SILK )</span>
          </a>

          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0 flex-row gap-2">
              {menus.map((item) => {
                const isActive = location.pathname === item.lnk;
                return (
                  <li className="nav-item" key={item.id_mn}>
                    <a
                      className={`nav-link-custom ${isActive ? 'active' : ''}`}
                      href={`#${item.des_mn.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => setActiveMenu(item.id_mn)}
                      style={{ ...styles.navLinkCustom, ...(isActive ? styles.navLinkActive : {}) }}
                    >
                      <i className={`bi ${getMenuIcon(item.des_mn)}`}></i>
                      {item.des_mn.toLowerCase().replace(/\b\w/g, s => s.toUpperCase())}
                    </a>
                  </li>
                );
              })}
            </ul>

            <div className="d-flex align-items-center text-white text-end gap-3 ms-auto">
              <div className="small border-end pe-3 border-light border-opacity-25 d-none d-sm-block">
                <div className="fw-semibold" style={{ fontSize: '0.8rem' }}>{time.date}</div>
                <div className="text-warning" style={{ fontSize: '0.7rem' }}>{time.clock}</div>
              </div>

              <Dropdown align="end">
                <Dropdown.Toggle
                  as="div" role="button"
                  className="d-flex align-items-center gap-2 px-2 py-1 rounded-3 custom-profile-toggle"
                  style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center bg-warning text-dark fw-bold rounded-circle shadow-sm"
                    style={{ width: '32px', height: '32px', fontSize: '0.85rem' }}
                  >
                    {username ? username.charAt(0).toUpperCase() : 'A'}
                  </div>
                  <div className="d-none d-md-block text-start">
                    <div className="text-white fw-semibold lh-1" style={{ fontSize: '0.85rem' }}>{username}</div>
                    <div className="text-white-50 mt-1" style={{ fontSize: '0.7rem' }}>Online</div>
                  </div>
                  <i className="bi bi-chevron-down text-white-50 ms-1 small"></i>
                </Dropdown.Toggle>

                <Dropdown.Menu className="shadow border-0 mt-2 py-2 style-dropdown-menu" style={{ minWidth: '180px', borderRadius: '10px' }}>
                  <Dropdown.Item onClick={handleLogout} className="py-2 text-danger small d-flex align-items-center gap-2 fw-semibold">
                    <i className="bi bi-box-arrow-right"></i> Keluar Aplikasi
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Container>
      </nav>

      {/* ══════════════ MAIN CONTENT ══════════════ */}
      <div style={styles.mainContent}>
        <Row className="g-3">
          <Col lg={12}>
            <Card style={styles.mainCard}>

              {/* Card Header */}
              <Card.Header style={{ ...styles.cardHeader, ...styles.bgMenuHeader }} className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-file-earmark-text-fill text-warning"></i>
                  <span>DATA SURAT PESANAN (SP)</span>
                  {location.state?.info && (
                    <span style={{
                      fontSize: '0.68rem',
                      fontWeight: 400,
                      color: 'rgba(255,255,255,0.75)',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '20px',
                      padding: '1px 10px',
                    }}>
                      <i className="bi bi-info-circle me-1"></i>{location.state.info}
                    </span>
                  )}
                </div>

                <div className="d-flex align-items-center gap-2">
                  <span className="badge rounded-pill fw-normal px-2.5 py-1" style={{ background: 'rgba(255,255,255,0.15)', fontSize: '0.72rem' }}>
                    {filteredSp.length} Surat Pesanan
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm d-inline-flex align-items-center gap-1 fw-semibold shadow-sm ms-2"
                    onClick={() => navigate('/home')}
                    style={styles.actionBtn}
                  >
                    <i className="bi bi-house-door"></i><span>Home</span>
                  </button>
                </div>
              </Card.Header>

              <Card.Body style={{ padding: '1rem' }}>
                {/* Toolbar & Total Omset Badge */}
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <InputGroup size="sm" style={{ maxWidth: '320px' }}>
                      <InputGroup.Text style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#64748b' }}>
                        <i className="bi bi-search" style={{ fontSize: '0.75rem' }}></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Cari No SP, pelanggan, lokasi, user..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ fontSize: '0.8rem', border: '1px solid #e2e8f0', borderLeft: 'none' }}
                      />
                      {searchQuery && (
                        <Button variant="outline-secondary" size="sm" onClick={() => setSearchQuery('')} style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}>
                          <i className="bi bi-x-lg"></i>
                        </Button>
                      )}
                    </InputGroup>

                    {/* Filter Lokasi */}
                    <Form.Select
                      size="sm"
                      value={selectedLokasiFilter}
                      onChange={(e) => setSelectedLokasiFilter(e.target.value)}
                      style={{ fontSize: '0.78rem', maxWidth: '210px', border: '1px solid #cbd5e1' }}
                    >
                      <option value="">Semua Lokasi / Apotek</option>
                      {Array.from(new Set(spList.map(s => s.nm_lokasi).filter(Boolean))).map(lok => (
                        <option key={lok} value={lok}>{lok}</option>
                      ))}
                    </Form.Select>
                  </div>

                  {/* Summary Total Badge & Refresh */}
                  <div className="d-flex align-items-center gap-2">
                    <div className="px-3 py-1 rounded-3 d-flex align-items-center gap-2"
                      style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', border: '1px solid #86efac' }}>
                      <i className="bi bi-cash-stack text-success fs-6"></i>
                      <div>
                        <div className="text-muted text-uppercase fw-semibold" style={{ fontSize: '0.62rem' }}>Total Nilai SP</div>
                        <div className="fw-bold text-success" style={{ fontSize: '0.85rem' }}>{formatRupiah(totalNominalSp)}</div>
                      </div>
                    </div>

                    <Button size="sm" variant="outline-secondary" onClick={fetchSp} disabled={isLoading}
                      style={{ fontSize: '0.75rem' }}>
                      <i className={`bi bi-arrow-clockwise me-1 ${isLoading ? 'spin' : ''}`}></i>
                      Refresh
                    </Button>
                  </div>
                </div>

                {/* Error Banner */}
                {fetchError && (
                  <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: '0.8rem' }}>
                    <i className="bi bi-exclamation-triangle-fill"></i>
                    <span>{fetchError}</span>
                    <button className="btn btn-sm btn-outline-danger ms-auto" onClick={fetchSp} style={{ fontSize: '0.7rem' }}>
                      Coba Lagi
                    </button>
                  </div>
                )}

                {/* Table */}
                <div className="table-responsive rounded-3 border shadow-sm" style={{ maxHeight: '62vh' }}>
                  <Table hover size="sm" className="align-middle mb-0" style={{ fontSize: '0.78rem' }}>
                    <thead className="sticky-top" style={{ zIndex: 10 }}>
                      <tr style={{
                        background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
                        color: '#0f172a',
                        fontSize: '0.8rem',
                        fontWeight: '800',
                        letterSpacing: '0.5px',
                        borderBottom: '3px solid #0d9488',
                        textTransform: 'uppercase',
                        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.05)'
                      }}>
                        <th className="px-3 py-3 text-center" style={{ color: '#475569', width: '55px', borderRight: '1px solid #cbd5e1' }}>No</th>
                        <th className="px-3 py-3 text-center" style={{ color: '#0284c7', minWidth: '180px', borderRight: '1px solid #cbd5e1' }}>
                          <i className="bi bi-file-earmark-text-fill me-1 text-primary"></i>No. Surat Pesanan (SP)
                        </th>
                        <th className="px-3 py-3" style={{ color: '#0f172a', minWidth: '240px', borderRight: '1px solid #cbd5e1' }}>
                          <i className="bi bi-building me-1 text-teal"></i>Nama Pelanggan
                        </th>
                        <th className="px-3 py-3" style={{ color: '#334155', minWidth: '180px', borderRight: '1px solid #cbd5e1' }}>
                          <i className="bi bi-geo-alt-fill me-1 text-danger"></i>Lokasi / Apotek
                        </th>
                        <th className="px-3 py-3 text-center" style={{ color: '#334155', width: '130px', borderRight: '1px solid #cbd5e1' }}>
                          <i className="bi bi-calendar-check-fill me-1 text-info"></i>Tgl Kirim
                        </th>
                        <th className="px-3 py-3 text-end" style={{ color: '#0f172a', minWidth: '140px', borderRight: '1px solid #cbd5e1' }}>
                          <i className="bi bi-calculator-fill me-1 text-success"></i>Total (Rp)
                        </th>
                        <th className="px-3 py-3 text-center" style={{ color: '#334155', width: '110px', borderRight: '1px solid #cbd5e1' }}>
                          <i className="bi bi-person-fill me-1 text-secondary"></i>User Input
                        </th>
                        <th className="px-3 py-3 text-center" style={{ color: '#0f172a', width: '100px' }}>
                          <i className="bi bi-eye-fill me-1 text-primary"></i>Aksi
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan="8" className="text-center py-5 text-muted">
                            <Spinner animation="border" size="sm" className="me-2" style={{ color: '#0d9488' }} />
                            Memuat data Surat Pesanan (SP)...
                          </td>
                        </tr>
                      ) : filteredSp.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-5 text-muted">
                            <i className="bi bi-inbox fs-3 d-block mb-1 text-secondary opacity-50"></i>
                            {searchQuery || selectedLokasiFilter ? 'Tidak ada Surat Pesanan yang cocok dengan pencarian.' : 'Belum ada data Surat Pesanan.'}
                          </td>
                        </tr>
                      ) : (
                        filteredSp.map((sp, idx) => (
                          <tr key={sp.no_sp || idx} style={{ transition: 'background 0.15s', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                            <td className="px-3 py-2 text-center text-muted fw-medium">{idx + 1}</td>

                            {/* No SP */}
                            <td className="px-3 py-2 text-center">
                              <Badge style={{ background: '#e0f2fe', color: '#0369a1', fontSize: '0.72rem', border: '1px solid #bae6fd', fontWeight: 700, padding: '4px 10px' }}>
                                {sp.no_sp}
                              </Badge>
                            </td>

                            {/* Pelanggan */}
                            <td className="px-3 py-2">
                              <div className="fw-semibold text-dark mb-0.5">{sp.nm_pelanggan}</div>
                              {sp.kd_pelanggan && (
                                <div style={{ fontSize: '0.7rem' }} className="text-muted">
                                  <i className="bi bi-tag me-1"></i>Kode: {sp.kd_pelanggan}
                                </div>
                              )}
                            </td>

                            {/* Lokasi */}
                            <td className="px-3 py-2 text-secondary">
                              <div className="fw-medium text-dark">{sp.nm_lokasi}</div>
                              {sp.lokasi && <div style={{ fontSize: '0.7rem' }} className="text-muted">Kode Lokasi: {sp.lokasi}</div>}
                            </td>

                            {/* Tanggal Kirim */}
                            <td className="px-3 py-2 text-center text-dark fw-medium" style={{ fontSize: '0.76rem' }}>
                              <i className="bi bi-calendar3 text-primary me-1"></i>
                              {sp.tgl_kirim}
                            </td>

                            {/* Total (Rp) */}
                            <td className="px-3 py-2 text-end fw-bold text-success" style={{ fontSize: '0.82rem' }}>
                              {formatRupiah(sp.total)}
                            </td>

                            {/* User Input */}
                            <td className="px-3 py-2 text-center">
                              <Badge style={{ background: '#f1f5f9', color: '#475569', fontSize: '0.68rem', border: '1px solid #cbd5e1' }}>
                                <i className="bi bi-person-circle me-1 text-secondary"></i>{sp.user_id}
                              </Badge>
                            </td>

                            {/* Action Tombol Detail */}
                            <td className="px-3 py-2 text-center">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => fetchDetailSp(sp)}
                                className="rounded-pill px-2.5 py-0.5 fw-semibold d-inline-flex align-items-center gap-1 shadow-sm"
                                style={{ fontSize: '0.7rem', border: '1px solid #0284c7' }}
                              >
                                <i className="bi bi-file-earmark-spreadsheet-fill"></i>
                                <span>Detail</span>
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Footer Info */}
                <div className="d-flex justify-content-between align-items-center mt-2.5 px-1 text-muted small" style={{ fontSize: '0.72rem' }}>
                  <span>Menampilkan <strong>{filteredSp.length}</strong> dari <strong>{spList.length}</strong> Surat Pesanan</span>
                  <span>Total Nominal: <strong className="text-success">{formatRupiah(totalNominalSp)}</strong> | Terakhir diperbarui: {time.clock}</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* ══════════════ MODAL RINCIAN DETAIL SP ══════════════ */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="xl"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0d9488 100%)', color: '#fff', padding: '0.75rem 1.25rem' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-spreadsheet-fill text-warning fs-5"></i>
            <span>RINCIAN DETAIL SURAT PESANAN (SP)</span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ background: '#f8fafc', padding: '1.25rem' }}>
          {selectedSp && (
            <Card className="border-0 shadow-sm mb-3" style={{ background: '#ffffff', borderRadius: '10px' }}>
              <Card.Body className="p-3">
                <Row className="g-2 align-items-center">
                  <Col md={3} sm={6}>
                    <div className="text-muted small fw-medium">No. Surat Pesanan (SP)</div>
                    <div className="fw-bold text-primary" style={{ fontSize: '0.9rem' }}>
                      <Badge style={{ background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', fontSize: '0.78rem' }}>
                        {selectedSp.no_sp}
                      </Badge>
                    </div>
                  </Col>
                  <Col md={4} sm={6}>
                    <div className="text-muted small fw-medium">Nama Pelanggan</div>
                    <div className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
                      <i className="bi bi-building me-1 text-teal"></i>{selectedSp.nm_pelanggan}
                    </div>
                  </Col>
                  <Col md={3} sm={6}>
                    <div className="text-muted small fw-medium">Lokasi / Apotek</div>
                    <div className="fw-semibold text-secondary" style={{ fontSize: '0.82rem' }}>
                      <i className="bi bi-geo-alt-fill me-1 text-danger"></i>{selectedSp.nm_lokasi}
                    </div>
                  </Col>
                  <Col md={2} sm={6}>
                    <div className="text-muted small fw-medium">Tgl Kirim</div>
                    <div className="fw-semibold text-dark" style={{ fontSize: '0.82rem' }}>
                      <i className="bi bi-calendar3 me-1 text-primary"></i>{selectedSp.tgl_kirim}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Metric Summary Cards di Dalam Modal */}
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="badge bg-white text-dark border px-3 py-2 shadow-sm fw-semibold" style={{ fontSize: '0.75rem' }}>
                <i className="bi bi-box-seam me-1 text-teal"></i>Total Item: <strong>{detailSpList.length}</strong> Obat
              </span>
              <span className="badge bg-white text-dark border px-3 py-2 shadow-sm fw-semibold" style={{ fontSize: '0.75rem' }}>
                <i className="bi bi-pie-chart-fill me-1 text-info"></i>Total Qty: <strong>{totalDetailQty.toLocaleString('id-ID')}</strong>
              </span>
            </div>

            <div className="px-3 py-1.5 rounded-3 d-flex align-items-center gap-2 shadow-sm"
              style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', border: '1px solid #86efac' }}>
              <i className="bi bi-cash-stack text-success fs-5"></i>
              <div>
                <div className="text-muted text-uppercase fw-semibold" style={{ fontSize: '0.62rem' }}>Grand Total Rincian</div>
                <div className="fw-bold text-success" style={{ fontSize: '0.95rem' }}>{formatRupiah(totalDetailNominal)}</div>
              </div>
            </div>
          </div>

          {/* Error Banner Modal */}
          {detailError && (
            <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: '0.8rem' }}>
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>{detailError}</span>
              <button className="btn btn-sm btn-outline-danger ms-auto" onClick={() => fetchDetailSp(selectedSp)} style={{ fontSize: '0.7rem' }}>
                Coba Lagi
              </button>
            </div>
          )}

          {/* Table Rincian Detail SP */}
          <div className="table-responsive rounded-3 border shadow-sm" style={{ maxHeight: '52vh', background: '#ffffff' }}>
            <Table hover size="sm" className="align-middle mb-0" style={{ fontSize: '0.78rem' }}>
              <thead className="sticky-top" style={{ zIndex: 10 }}>
                <tr style={{
                  background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
                  color: '#0f172a',
                  fontSize: '0.78rem',
                  fontWeight: '800',
                  letterSpacing: '0.4px',
                  borderBottom: '3px solid #0d9488',
                  textTransform: 'uppercase'
                }}>
                  <th className="px-3 py-2.5 text-center" style={{ color: '#475569', width: '50px', borderRight: '1px solid #cbd5e1' }}>No</th>
                  <th className="px-3 py-2.5 text-center" style={{ color: '#0284c7', width: '120px', borderRight: '1px solid #cbd5e1' }}>
                    <i className="bi bi-tag-fill me-1 text-primary"></i>Kode Obat
                  </th>
                  <th className="px-3 py-2.5" style={{ color: '#0f172a', minWidth: '200px', borderRight: '1px solid #cbd5e1' }}>
                    <i className="bi bi-capsule me-1 text-teal"></i>Nama Obat
                  </th>
                  <th className="px-3 py-2.5 text-center" style={{ color: '#334155', width: '130px', borderRight: '1px solid #cbd5e1' }}>
                    <i className="bi bi-box-seam me-1 text-info"></i>Qty & Satuan
                  </th>
                  <th className="px-3 py-2.5 text-end" style={{ color: '#334155', width: '130px', borderRight: '1px solid #cbd5e1' }}>
                    <i className="bi bi-currency-dollar me-1 text-success"></i>Harsat (Rp)
                  </th>
                  <th className="px-3 py-2.5 text-center" style={{ color: '#334155', width: '90px', borderRight: '1px solid #cbd5e1' }}>
                    <i className="bi bi-arrow-repeat me-1 text-secondary"></i>Konversi
                  </th>
                  <th className="px-3 py-2.5 text-center" style={{ color: '#334155', width: '110px', borderRight: '1px solid #cbd5e1' }}>
                    <i className="bi bi-calendar3 me-1 text-primary"></i>Tanggal
                  </th>
                  <th className="px-3 py-2.5 text-end" style={{ color: '#0f172a', width: '140px' }}>
                    <i className="bi bi-calculator-fill me-1 text-success"></i>Subtotal (Rp)
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoadingDetail ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5 text-muted">
                      <Spinner animation="border" size="sm" className="me-2" style={{ color: '#0d9488' }} />
                      Memuat rincian detail barang SP...
                    </td>
                  </tr>
                ) : detailSpList.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5 text-muted">
                      <i className="bi bi-inbox fs-3 d-block mb-1 text-secondary opacity-50"></i>
                      Tidak ada rincian obat ditemukan untuk Surat Pesanan ini.
                    </td>
                  </tr>
                ) : (
                  detailSpList.map((d, idx) => (
                    <tr key={d.kd_obat || idx} style={{ transition: 'background 0.15s', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                      <td className="px-3 py-2 text-center text-muted fw-medium">{idx + 1}</td>

                      {/* Kode Obat */}
                      <td className="px-3 py-2 text-center">
                        <Badge style={{ background: '#fef3c7', color: '#b45309', fontSize: '0.68rem', border: '1px solid #fde68a', fontWeight: 700 }}>
                          {d.kd_obat}
                        </Badge>
                      </td>

                      {/* Nama Obat */}
                      <td className="px-3 py-2 fw-semibold text-dark">
                        <i className="bi bi-capsule-pill me-1.5 text-teal"></i>{d.nm_obat}
                      </td>

                      {/* Qty & Satuan */}
                      <td className="px-3 py-2 text-center">
                        <span className="fw-bold text-primary">{d.qty}</span>{' '}
                        <span className="text-secondary small">{d.satuanjual || d.satuanbeli || ''}</span>
                      </td>

                      {/* Harsat */}
                      <td className="px-3 py-2 text-end text-dark">
                        {formatRupiah(d.harsat)}
                      </td>

                      {/* Konversi */}
                      <td className="px-3 py-2 text-center text-secondary">
                        {d.konversi || 1}
                      </td>

                      {/* Tanggal */}
                      <td className="px-3 py-2 text-center text-secondary" style={{ fontSize: '0.73rem' }}>
                        {d.tanggal}
                      </td>

                      {/* Subtotal (total) */}
                      <td className="px-3 py-2 text-end fw-bold text-success">
                        {formatRupiah(d.total)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>

        <Modal.Footer style={{ background: '#f1f5f9', borderTop: '1px solid #e2e8f0', padding: '0.6rem 1.25rem' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowDetailModal(false)} className="px-3">
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

// ══════════════ STYLES ══════════════
const styles = {
  appContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 40%, #e2e8f0 100%)',
    display: 'flex',
    flexDirection: 'column',
  },
  navbarCustom: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0d9488 100%)',
    padding: '0.4rem 1rem',
    flexShrink: 0,
  },
  navLinkCustom: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: '0.78rem',
    fontWeight: 500,
    padding: '0.3rem 0.6rem',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    textDecoration: 'none',
    transition: 'all 0.2s',
  },
  navLinkActive: {
    color: '#ffffff',
    background: 'rgba(255,255,255,0.12)',
  },
  mainContent: {
    flex: 1,
    padding: '0.8rem',
    overflowY: 'auto',
  },
  mainCard: {
    borderRadius: '14px',
    border: '1px solid #cbd5e1',
    boxShadow: '0 10px 30px -5px rgba(15, 23, 42, 0.08), 0 0 15px rgba(13, 148, 136, 0.04)',
    background: '#ffffff',
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '0.65rem 1rem',
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '0.85rem',
  },
  bgMenuHeader: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0d9488 100%)',
  },
  actionBtn: {
    fontSize: '0.73rem',
    borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.3)',
    padding: '0.25rem 0.6rem',
  },
};

export default DataSp;
